/**
 * @deprecated Use `AutopayCard` from `@/components/residents/redesign/payments` instead.
 * This component uses platform-branching inline styles and will be removed after full migration.
 */
import React from 'react';
import { View, StyleSheet, Image, TouchableOpacity, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { useAppSelector } from '@/store';
import { useGetLeaseQuery, useGetPaymentMethodsQuery } from '@/store/api';
import { getCardConnectCardName } from '@/utils/residents/paymentUtils';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useDesktopColors } from '@/constants/residents/desktop/color';
import { useMobileColors } from '@/constants/residents/mobile/color';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { StackingRow } from '@/components/shared/redesign/StackingRow';
import { DesktopOverflowText } from '@/components/common/admin/desktop/DesktopOverflowText';
import { useToast } from '@/contexts/admin/ToastContext';
import { strings } from '@/constants/residents';
import { useResponsive } from '@/hooks/common/useResponsive';
import { showConfirm } from '@/utils/common/alert';

const mapLeaseStatus = (status?: string) => {
  switch (status) {
    case 'current':
      return 'Current Lease';
    case 'past':
      return 'Past Lease';
    case 'future':
      return 'Future Lease';
    case 'notice':
      return 'Lease On Notice';
    default:
      return 'Current Lease';
  }
};

type Props = {
  leaseId: number;
  user: any | null;
  onManage: () => void;
  onRemove: (autopayId: number) => Promise<void> | void;
  onRefresh: () => void;
  refreshNonce: number;
};

export const LeaseAutopayCard: React.FC<Props> = ({ leaseId, user, onManage, onRemove, onRefresh, refreshNonce }) => {
  const { showSuccess, showError } = useToast();
  const { isWeb, layoutMode } = useResponsive();
  const isDesktop = isWeb && layoutMode === 'desktop';
  const DC = useDesktopColors();
  const MC = useMobileColors();
  const { semantic, components } = useThemeColors();

  // Use desktop colors on web desktop, mobile colors otherwise (both theme-aware)
  const colors = isDesktop ? DC : MC;
  
  const { data: leaseResp, refetch: refetchLease } = useGetLeaseQuery({ leaseId, includeTransactions: false, includePayments: true }, { skip: !leaseId });
  const lease = (leaseResp as any)?.response?.lease;

  React.useEffect(() => {
    refetchLease();
  }, [refreshNonce, refetchLease]);

  const propertyName = lease?.property?.marketingName || '—';
  const primaryUser = (lease?.users || []).find((u: any) => u?.LeaseCustomer?.customerType === 'primary');
  const firstName = user?.firstName ?? primaryUser?.firstName;
  const lastName = user?.lastName ?? primaryUser?.lastName;
  const email = user?.email ?? primaryUser?.email ?? '—';
  const displayName = (firstName || lastName) ? `${firstName || ''} ${lastName || ''}`.trim() : email;
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const photoUrl = primaryUser?.photoUrl || user?.photoUrl || (authUser as any)?.profilePictureUrl || '';

  const autopay = Array.isArray(lease?.recurringPayments) && lease.recurringPayments.length > 0 ? lease.recurringPayments[0] : undefined;
  const hasAutopay = Boolean(autopay);

  const { data: pmResp } = useGetPaymentMethodsQuery();
  const methods: any[] = (pmResp as any)?.response || (pmResp as any) || [];
  const pm = methods.find((m: any) => String(m.sourceId || m.id) === String(autopay?.sourceId));
  const paymentMethodString = pm ? `${getCardConnectCardName(pm.accttype || '')}${pm.last4 ? ` ending in ${pm.last4}` : ''}` : '—';

  const leaseStatusText = mapLeaseStatus(lease?.statusType);

  const formatDate = (iso?: string) => {
    if (!iso) return 'End of lease';
    try {
      const d = new Date(iso);
      const first = new Date(d);
      first.setDate(1);
      return first.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  const firstPayment = (() => {
    const created = autopay?.createdAt || autopay?.startDate;
    if (!created) return '—';
    const next = new Date(created);
    next.setMonth(next.getMonth() + 1);
    next.setDate(1);
    return next.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  })();

  const lastPayment = formatDate(autopay?.endDate);
  const amountText = autopay?.amount ? `$${Number(autopay.amount).toFixed(2)}` : 'Full balance';

  const isSetupDisabled = (() => {
    const end = lease?.endDate ? new Date(lease.endDate) : null;
    if (!end || isNaN(end.getTime())) return false;
    const firstOfEndMonth = new Date(end);
    firstOfEndMonth.setDate(1);
    const today = new Date();
    return firstOfEndMonth < today;
  })();

  const handleRemoveAutopay = async () => {
    if (!autopay?.id) return;

    const confirmed = await showConfirm(
      'Remove Autopay',
      'Are you sure you want to remove autopay for this lease? You will need to make manual payments going forward.',
      'Remove Autopay',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await onRemove(autopay.id);
      onRefresh();
      await refetchLease();
      showSuccess('Autopay removed successfully');
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error?.message || 'Failed to remove autopay');
    }
  };

  return (
    <View style={[styles.summaryCard, { 
      backgroundColor: isDesktop ? DC.PRIMARY_BACKGROUND : components.card.background,
      borderWidth: isDesktop ? 1 : 0,
      borderColor: isDesktop ? DC.TEXT_TERTIARY : 'transparent',
      shadowColor: isDesktop ? 'transparent' : '#000',
      shadowOffset: isDesktop ? { width: 0, height: 0 } : { width: 0, height: 1 },
      shadowOpacity: isDesktop ? 0 : 0.06,
      shadowRadius: isDesktop ? 0 : 2,
      elevation: isDesktop ? 0 : 2,
    }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View style={[styles.avatarCircle, { 
            backgroundColor: components.avatar.background,
          }]}>
            {photoUrl ? (
              <Image source={{ uri: photoUrl }} style={styles.avatarImg} />
            ) : (
              <RESIDENTS_SVG_COMPONENTS.PROFILE_PLACEHOLDER width={'50%'} height={'50%'} />
            )}
          </View>
          <View>
            <View style={[styles.badge, {
              backgroundColor: isDesktop ? DC.CIRCLE_ICON_BACKGROUND : semantic.background.tertiary,
              borderWidth: isDesktop ? 1 : 0,
              borderColor: isDesktop ? semantic.border.secondary : 'transparent',
            }]}>
              <Text style={[styles.badgeText, { color: colors.TEXT_PRIMARY }]}>{leaseStatusText}</Text>
            </View>
            <Text style={[styles.userTitle, { color: colors.TEXT_PRIMARY }]}>{displayName}</Text>
            <Text style={[styles.propertyText, { color: colors.TEXT_TERTIARY, fontSize: 14 }]}>{propertyName}</Text>
          </View>
        </View>
        {Platform.OS === 'web' ? (
          <View style={{ width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
            <DesktopOverflowText
              text=""
              title=""
              tooltipText={strings.AUTOPAY_CHARGES_TOOLTIP}
              numberOfLines={1}
              icon={<RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE width={20} height={20} color={colors.TEXT_TERTIARY} />}
            />
          </View>
        ) : (
          <View accessibilityLabel={strings.AUTOPAY_CHARGES_TOOLTIP} accessible>
            <RESIDENTS_SVG_COMPONENTS.INFORMATION_CIRCLE width={20} height={20} color={colors.TEXT_TERTIARY} />
          </View>
        )}
      </View>

      {hasAutopay ? (
        <View style={{ marginTop: 16 }}>
          <StackingRow style={styles.summaryRow} stackedStyle={styles.summaryRowStacked}>
            <Text style={[styles.summaryLabel, { color: colors.TEXT_TERTIARY }]}>Payment Method</Text>
            <Text style={[styles.summaryValue, { color: colors.TEXT_PRIMARY }]}>{paymentMethodString}</Text>
          </StackingRow>
          <StackingRow style={styles.summaryRow} stackedStyle={styles.summaryRowStacked}>
            <Text style={[styles.summaryLabel, { color: colors.TEXT_TERTIARY }]}>Autopay amount</Text>
            <Text style={[styles.summaryValue, { color: colors.TEXT_PRIMARY }]}>{amountText}</Text>
          </StackingRow>
          <StackingRow style={styles.summaryRow} stackedStyle={styles.summaryRowStacked}>
            <Text style={[styles.summaryLabel, { color: colors.TEXT_TERTIARY }]}>First payment</Text>
            <Text style={[styles.summaryValue, { color: colors.TEXT_PRIMARY }]}>{firstPayment}</Text>
          </StackingRow>
          <StackingRow style={styles.summaryRow} stackedStyle={styles.summaryRowStacked}>
            <Text style={[styles.summaryLabel, { color: colors.TEXT_TERTIARY }]}>Last payment</Text>
            <Text style={[styles.summaryValue, { color: colors.TEXT_PRIMARY }]}>{lastPayment}</Text>
          </StackingRow>
          <View style={{ flexDirection: 'row', gap: 20, marginTop: 16 }}>
            <TouchableOpacity onPress={() => { onManage(); onRefresh(); }}>
              <Text style={{ 
                color: isDesktop ? DC.TEXT_SECONDARY : MC.BUTTON_PRIMARY,
                fontWeight: '600',
                fontSize: 14,
              }}>Edit settings</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleRemoveAutopay}>
              <Text style={{ 
                color: isDesktop ? DC.BUTTON_TERTIARY : semantic.status.error,
                fontWeight: '600',
                fontSize: 14,
              }}>Remove</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        <View style={{ marginTop: 16 }}>
          <TouchableOpacity 
            style={[styles.primaryBtn, { 
              backgroundColor: isDesktop ? DC.BUTTON_TERTIARY : MC.BUTTON_PRIMARY,
            }, isSetupDisabled && { opacity: 0.5 }]}
            onPress={onManage} 
            disabled={isSetupDisabled}
          >
            <Text style={[styles.primaryBtnText, { 
              color: components.button.primaryText,
            }]}>Setup Autopay</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  summaryCard: { 
    borderRadius: 12, 
    padding: 16, 
    gap: 10,
  },
  summaryRow: { 
    flexDirection: 'row', 
    justifyContent: 'space-between', 
    paddingVertical: 6,
  },
  summaryLabel: { 
    fontSize: 14,
  },
  summaryRowStacked: {
    gap: 2,
  },
  summaryValue: { 
    flexShrink: 1,
    textAlign: 'right',
    fontSize: 14, 
    fontWeight: '500',
  },
  avatarCircle: { 
    width: 48, 
    height: 48, 
    borderRadius: 24, 
    alignItems: 'center', 
    justifyContent: 'center', 
    overflow: 'hidden',
  },
  avatarImg: { 
    width: 48, 
    height: 48,
  },
  badge: { 
    alignSelf: 'flex-start', 
    paddingHorizontal: 8, 
    paddingVertical: 4, 
    borderRadius: 9999, 
    marginBottom: 4,
  },
  badgeText: { 
    fontSize: 12,
    fontWeight: '600',
  },
  userTitle: { 
    fontWeight: '700', 
    marginBottom: 4,
    fontSize: 16,
  },
  propertyText: { 
    fontSize: 14,
  },
  primaryBtn: { 
    paddingVertical: 12, 
    paddingHorizontal: 24, 
    borderRadius: 8, 
    alignSelf: 'flex-start',
  },
  primaryBtnText: { 
    fontWeight: '600',
    fontSize: 16,
  },
});

export default LeaseAutopayCard;


